import type { AttentionMode, AttentionThresholds } from 'eyecontact'
import { formatMs } from '../lib/cn'
import { isCustomised, type Settings } from '../lib/settings'
import { Button, SegmentedControl, Slider, Switch, Tooltip } from './ui'

export type { Settings }

export function SettingsPanel({
  settings,
  onChange,
  onReset,
  disabled,
}: {
  settings: Settings
  onChange: (patch: Partial<Settings>) => void
  onReset: () => void
  disabled?: boolean
}) {
  const t = settings.thresholds
  const setThreshold = (patch: Partial<AttentionThresholds>) =>
    onChange({ thresholds: { ...t, ...patch } })

  return (
    <div className="space-y-5 p-3.5">
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-2xs font-semibold uppercase tracking-[0.14em] text-faint">
            Detection mode
          </span>
          <Tooltip label="Presence only checks that a face is there and roughly front-on. Gaze also tracks where the eyes are pointing, so it catches you facing the screen while reading your phone.">
            <button
              type="button"
              aria-label="What the detection modes mean"
              className="grid h-3.5 w-3.5 cursor-help place-items-center rounded-full border border-line text-[9px] text-faint hover:border-accent/50 hover:text-accent"
            >
              ?
            </button>
          </Tooltip>
        </div>
        <SegmentedControl<AttentionMode>
          label="Detection mode"
          value={settings.mode}
          onChange={(mode) => onChange({ mode })}
          options={[
            { value: 'gaze', label: 'Gaze + head', hint: 'Strict: tracks eyeball direction' },
            { value: 'presence', label: 'Presence', hint: 'Loose: face in frame, roughly front-on' },
          ]}
        />
      </div>

      <div className="space-y-4 border-t border-line pt-4">
        <Slider
          label="Grace before pause"
          value={settings.graceMs}
          min={500}
          max={10000}
          step={250}
          onChange={(graceMs) => onChange({ graceMs })}
          format={formatMs}
          hint="How long you can look away before playback stops."
        />
        <Slider
          label="Hold before resume"
          value={settings.resumeMs}
          min={0}
          max={2000}
          step={50}
          onChange={(resumeMs) => onChange({ resumeMs })}
          format={formatMs}
          hint="Sustained attention needed to restart. Raise this if it flaps."
        />
      </div>

      <div className="space-y-4 border-t border-line pt-4">
        <Slider
          label="Gaze tolerance"
          value={t.gazeTolerance}
          min={0.1}
          max={0.9}
          step={0.01}
          onChange={(gazeTolerance) => setThreshold({ gazeTolerance })}
          format={(v) => v.toFixed(2)}
          hint="How far the eyes may drift from centre. Raise it for a large monitor."
        />
        <Slider
          label="Head yaw tolerance"
          value={t.yawToleranceDeg}
          min={5}
          max={60}
          step={1}
          onChange={(yawToleranceDeg) => setThreshold({ yawToleranceDeg })}
          format={(v) => `${v}°`}
        />
        <Slider
          label="Head pitch tolerance"
          value={t.pitchToleranceDeg}
          min={5}
          max={50}
          step={1}
          onChange={(pitchToleranceDeg) => setThreshold({ pitchToleranceDeg })}
          format={(v) => `${v}°`}
        />
        <Slider
          label="Blink cutoff"
          value={t.blinkThreshold}
          min={0.2}
          max={0.95}
          step={0.01}
          onChange={(blinkThreshold) => setThreshold({ blinkThreshold })}
          format={(v) => v.toFixed(2)}
          hint="Frames above this are ignored, so blinking never pauses the video."
        />
      </div>

      <div className="space-y-4 border-t border-line pt-4">
        <Slider
          label="Detection rate"
          value={settings.fps}
          min={4}
          max={30}
          step={1}
          onChange={(fps) => onChange({ fps })}
          format={(v) => `${v} fps`}
          hint="Above ~15 you burn CPU for almost no accuracy."
        />
        <Switch
          label="Pause on tab switch"
          checked={settings.pauseWhenTabHidden}
          onChange={(pauseWhenTabHidden) => onChange({ pauseWhenTabHidden })}
          hint="A hidden tab keeps playing audio otherwise. Needs a restart to take effect."
        />
        <Switch
          label="Pause on app switch"
          checked={settings.pauseWhenWindowBlurred}
          onChange={(pauseWhenWindowBlurred) => onChange({ pauseWhenWindowBlurred })}
          hint="Cmd-tabbing away counts as looking away. Turn off to keep playing while you work. Needs a restart."
        />
        <Switch
          label="Draw face mesh"
          checked={settings.showMesh}
          onChange={(showMesh) => onChange({ showMesh })}
          hint="Overlay landmarks on the preview. Needs a restart to take effect."
        />
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-line pt-3">
        <p className="text-2xs leading-relaxed text-faint">
          {isCustomised(settings)
            ? 'Your tuning is saved in this browser.'
            : 'Using the shipped defaults.'}
        </p>
        <Button variant="ghost" onClick={onReset} disabled={!isCustomised(settings)}>
          Reset
        </Button>
      </div>

      {disabled && (
        <p className="text-2xs text-faint">
          Thresholds and timing apply live. Mesh and detection rate changes need a restart.
        </p>
      )}
    </div>
  )
}
